import { getSpecialtyById } from '@services/queries/specialtyQueries';
import { isDefined } from '@utils/isDefined';
import uniq from 'lodash/uniq';
import compact from 'lodash/compact';
import orderBy from 'lodash/orderBy';
import cloneDeep from 'lodash/cloneDeep';

import type { EntryPdfSignatureQuery } from 'chaika-types';
import {
    DictionaryParams,
    EntryPrintFormBlockData,
    EntryPrintFormDataParams,
    EntryPrintFormFieldsData,
    EntryPrintFormSectionGroupsData,
    EntryPrintFormTitleData,
    EntryTypeFieldName,
    RecommendationData,
    ReferralStatus,
} from 'chaika-frontend-types/frontend-cms';
import { DictionaryNames, DrugStatus, EntryTypeFieldType, EntryTypeType, Locale } from 'chaika-enums';

import { store } from '@store';
import { queryClient } from '@store/queryClient';
import { Entry, SoapEntryTypeNames } from '@patient/store/entry/Entry';
import { Ehr } from '@patient/store/ehr/Ehr';
import { getCachedUserById } from '@user/queries/userQueries';
import { userProfileQueryOptions } from '@user/queries/userProfileQueries';
import { getFullName } from '@user/utils/getFullName';
import { getInitials } from '@user/utils/getInitials';
import { isDoctor } from '@user/utils/isDoctor';
import { formatFullName } from '@utils/formatFullName';
import { getEntryExtraData } from '@patient/ui/shared/Entry/helpers/getEntryExtraData';
import { EntryTypeSection } from '@template/store/entryType/EntryTypeSection';
import { EntryTypeBlock } from '@template/store/entryType/EntryTypeBlock';
import { ENTRY_MEDICAL_CASE_BLOCK_ID } from '@store/types';
import { ENTRY_FIELD_EXCEPTIONS } from '@template/store/entryType/types';
import { getTranslate, getTranslatesForAllLocales } from '@chaika/i18n/helper';
import { EntryPrintFormParamsHelper, includedIds } from '@patient/services/entry/EntryPrintFormParamsHelper';
import { staticEntryTypeService } from '@patient/services/entry/staticEntryTypeService';
import { DateFormat, DateHelper } from '@utils/DateHelper';
import { MedicationField, ReferralField } from '@store/models/EntryFields';
import { getVisibleUsedMedications } from '@store/models/SystemFields/Medications';
import { outwardReferralToItemParams, referralToItemParams } from '@common/TreatmentPlan/utils/treatmentPlanData';
import { isOutwardReferralOfEntry } from '@common/TreatmentPlan/utils/outwardReferralFilter';
import { ItemType } from '@common/TreatmentPlan/types';
import { TranslationValues } from 'chaika-translation-data';

interface Params {
    isSoap?: boolean;
    isShortPrintForm?: boolean;
    isObjectiveForm?: boolean;
    ignoreHideFromPatient?: boolean;
    signature?: EntryPdfSignatureQuery;
}

export enum BlockShowingMode {
    Current = 'current',
    Other = 'other',
}

class EntryPrintFormDataHelper {
    private entry: Entry = store.entry;
    private ehr: Ehr = store.ehr;

    private includedTitles: string[] = [
        ...getTranslatesForAllLocales('diagnosis'),
        ...getTranslatesForAllLocales('referralCut'),
        ...getTranslatesForAllLocales('conclusionCut'),
        ...getTranslatesForAllLocales('resultOfCut'),
    ].map(title => title.toLowerCase());

    private excludeTitles: string[] = [...getTranslatesForAllLocales('services')].map(title => title.toLowerCase());

    public makeEntryPrintData(params: Params): EntryPrintFormDataParams {
        const logoUrl = this.getLogoUrl();
        const { entryType, doctorId, authorId, coAuthorIds = [] } = this.entry;
        const isEntryTypeTests = entryType.type === EntryTypeType.Tests;

        const author = getCachedUserById(queryClient, doctorId ?? authorId);
        const coAuthors = coAuthorIds.map(id => getCachedUserById(queryClient, id));
        const authors = compact([author, ...coAuthors]).filter(user => isDoctor(user));
        const authorShortNames = uniq(authors.map(user => getInitials(user))).join(', ');

        return {
            doctorShort: authorShortNames.length > 0 ? authorShortNames : undefined,
            signature: params.signature,
            files: [],
            logoUrl: logoUrl !== '' ? logoUrl : undefined,
            tittleParams: this.getTitleParams(),
            episodeNames: isEntryTypeTests
                ? [this.translateBySelectedLang('analyzes')]
                : params.isSoap
                  ? []
                  : this.getEpisodeParams(),
            laboratoryEntryAnalysisNames: isEntryTypeTests ? this.getEntryAnalysisNames() : undefined,
            sectionGroups: params.isSoap
                ? this.getSoapSectionsForPrint(params)
                : [
                      {
                          sections: this.getSectionsForPrint(params).map(section =>
                              EntryPrintFormParamsHelper.makeSectionParams(section, params),
                          ),
                      },
                  ],
        };
    }

    private getLogoUrl(): string {
        const { legalEntityBranchList, legalEntityList } = store;
        const legalEntityId = legalEntityBranchList.getActualOnDayLegalEntityId(
            this.entry.organizationId,
            this.entry.creationTime,
        );
        const legalEntity = legalEntityId ? legalEntityList.get(legalEntityId) : undefined;

        return legalEntity?.logoUrl ?? '';
    }

    private getTitleParams(): EntryPrintFormTitleData {
        const { entryType, isMedicalCasesHiddedOnPrint, medicalCases, selectedLang } = this.entry;

        const entryTypeTitle = entryType?.title ?? '';
        const title = selectedLang
            ? (entryType.titleTranslationsValues.find(it => it.locale === selectedLang)?.value ?? entryTypeTitle)
            : entryTypeTitle;

        return {
            title,
            reasonForAppeal: isMedicalCasesHiddedOnPrint
                ? undefined
                : medicalCases.values.map(({ name }) => name).join(','),
            patientDetail: this.makePatientDetail(),
            entryDetail: this.makeEntryDetail(),
        };
    }

    private getEpisodeParams(): string[] {
        const { medicalCases } = this.entry;

        return medicalCases.values.map(it => it.name);
    }

    private getEntryAnalysisNames(): string[] {
        const { serviceStore, entry } = store;

        const serviceIds = uniq(entry.renderedServicesList.values.map(it => it.serviceId) ?? []);
        const services = compact(serviceIds.map(serviceId => serviceStore.serviceList.get(serviceId)));

        return services.map(service => service.name);
    }

    private getSectionsForPrint({ isShortPrintForm }: Params = {}): EntryTypeSection[] {
        return this.filterSections(this.entry.entryType.sectionsWithDataAndAlwaysShow, { isShortPrintForm });
    }

    private getSoapSectionsForPrint(params: Params = {}): EntryPrintFormSectionGroupsData[] {
        const {
            entry: {
                entryType: { type },
            },
        } = store;
        const isDiagnosticEntry = type === EntryTypeType.Diagnostics || type === EntryTypeType.UsDiagnostics;
        const isAnalysisEntry = type === EntryTypeType.Tests;

        const subjectiveData = isDiagnosticEntry
            ? undefined
            : {
                  title: this.translateBySelectedLang('subjective'),
                  sections: this.filterSections(
                      staticEntryTypeService.getSectionsByTabName(SoapEntryTypeNames.SubjectiveEntryType, this.ehr.sex),
                      params,
                  ).map(it =>
                      EntryPrintFormParamsHelper.makeSectionParams(
                          {
                              ...it,
                              blocks: getBlocks([it]),
                          },
                          params,
                      ),
                  ),
              };

        const objectiveData = {
            title: this.translateBySelectedLang('objective'),
            sections: this.filterSections(staticEntryTypeService.getObjectiveSections(this.ehr.sex), params).map(it =>
                EntryPrintFormParamsHelper.makeSectionParams(
                    {
                        ...it,
                        blocks: getBlocks([it]),
                    },
                    params,
                ),
            ),
        };

        const assessmentData = {
            title: this.translateBySelectedLang('assessmentLikeEpisodes'),
            sections: this.filterSections(
                staticEntryTypeService.getSectionsByTabName(SoapEntryTypeNames.AssessmentEntryType, this.ehr.sex),
                params,
            ).map(it =>
                EntryPrintFormParamsHelper.makeSectionParams(
                    {
                        ...it,
                        blocks: getBlocks([it]),
                    },
                    params,
                ),
            ),
        };

        if (assessmentData.sections.length === 0) {
            assessmentData.sections.push({ blocks: this.getAssessmentEpisodeBlocks() });
        } else {
            assessmentData.sections?.[0].blocks.unshift(...this.getAssessmentEpisodeBlocks());
        }

        const treatmentPlanData =
            isDiagnosticEntry || isAnalysisEntry ? undefined : this.getTreatmentPlanSection(params);

        const billingData = {
            title: this.translateBySelectedLang('services'),
            sections: this.filterSections(
                staticEntryTypeService.getSectionsByTabName(SoapEntryTypeNames.PaymentEntryType, this.ehr.sex),
                params,
            ).map(it =>
                EntryPrintFormParamsHelper.makeSectionParams(
                    {
                        ...it,
                        blocks: getBlocks([it]),
                    },
                    params,
                ),
            ),
        };

        let sectionGroups: EntryPrintFormSectionGroupsData[];

        if (params.isShortPrintForm) {
            sectionGroups = compact([assessmentData, treatmentPlanData]);
        } else if (params.isObjectiveForm) {
            sectionGroups = [objectiveData];
        } else {
            sectionGroups = compact([subjectiveData, objectiveData, assessmentData, treatmentPlanData, billingData]);
        }

        return sectionGroups.filter(tab => tab.sections.some(section => section.blocks.length > 0));
    }

    private filterSections(sections: EntryTypeSection[], params: Params = {}): EntryTypeSection[] {
        return sections.filter(section => {
            const hideInShortPrintForm = params.isShortPrintForm ? this.isSectionHideInShortPrintForm(section) : false;
            return (
                !hideInShortPrintForm &&
                section.blocks.some(
                    item =>
                        getTranslatesForAllLocales('services').every(
                            title => item.title.toLowerCase() !== title.toLowerCase(),
                        ) &&
                        (item.alwaysShow ||
                            item.hasData ||
                            item.fields.some(f => f.type === EntryTypeFieldType.DentalTreatment)),
                )
            );
        });
    }

    private isSectionHideInShortPrintForm({ title, blockIds, blocks }: EntryTypeSection): boolean {
        const titleLower = title.toLowerCase();

        const isTitleNotIncluded = this.includedTitles.every(name => !titleLower.includes(name));
        const allBlocksHiddenInShortPrint = blocks.every(block => this.isBlockHideInShortPrintForm(block, true));
        const allBlocksWithoutData = blocks.every(block => !block.hasData);
        const hasMedicalCaseBlock = new Set(blockIds).has(ENTRY_MEDICAL_CASE_BLOCK_ID);

        const isTitleExplicitlyExcluded = this.excludeTitles.some(name => titleLower === name);
        const allBlocksInvisible = blocks.every(block => !(block.hasData || block.isEnableOnViewMode));
        const isSomeBlockIsNotHidden = !blocks.some(block => !this.isBlockHideInShortPrintForm(block, true));

        const shouldHideByInclusionAndContent =
            isTitleNotIncluded && (allBlocksHiddenInShortPrint || allBlocksWithoutData) && !hasMedicalCaseBlock;

        return (
            isSomeBlockIsNotHidden || shouldHideByInclusionAndContent || isTitleExplicitlyExcluded || allBlocksInvisible
        );
    }

    private isBlockHideInShortPrintForm(
        { title, fieldNames, id }: Pick<EntryTypeBlock, 'title' | 'fieldNames' | 'id'>,
        isShortPrintForm: boolean = false,
    ): boolean {
        return isShortPrintForm
            ? (this.includedTitles.every(name => !title.toLowerCase().includes(name)) &&
                  includedIds.every(includedId => includedId !== id) &&
                  !fieldNames.some(name => ENTRY_FIELD_EXCEPTIONS.includes(name as EntryTypeFieldType))) ||
                  this.excludeTitles.some(hideTitle => hideTitle === title.toLowerCase())
            : false;
    }

    private makePatientDetail(): EntryPrintFormFieldsData[] {
        const { fullName, birthDate, ageString, ageStringEng, sexString, engSexString, engFullName, recordId } =
            this.ehr;
        const { selectedLang } = this.entry;

        let ageStr = '';
        let sexStr = '';
        let fullNameStr = '';

        const lang = selectedLang || store.authUser.selectedLanguage;

        if (lang === Locale.Ru || lang === Locale.Am) {
            ageStr = ageString;
            sexStr = sexString;
            fullNameStr = fullName;
        } else {
            ageStr = ageStringEng;
            sexStr = engSexString;
            fullNameStr = !!engFullName.trim() ? engFullName : fullName;
        }

        return [
            {
                title: this.translateBySelectedLang('patient'),
                value: fullNameStr,
            },
            {
                title: this.translateBySelectedLang('age'),
                value: `${birthDate}, ${ageStr}`,
            },
            {
                title: this.translateBySelectedLang('sex'),
                value: sexStr,
            },
            {
                title: this.translateBySelectedLang('recordId'),
                value: `${recordId}`,
            },
        ];
    }

    private getTreatmentPlanSection(params: Params): EntryPrintFormSectionGroupsData {
        const treatmentPlanBlocks = [
            this.getReferralBlock(),
            this.getMedicationAndRecommendationBlock(BlockShowingMode.Other),
            this.getMedicationAndRecommendationBlock(BlockShowingMode.Current),
        ];

        const treatmentPlanSection = {
            title: this.translateBySelectedLang('treatmentPlan'),
            sections: this.filterSections(
                staticEntryTypeService.getSectionsByTabName(SoapEntryTypeNames.PlanEntryType, this.ehr.sex),
                params,
            ).map(it =>
                EntryPrintFormParamsHelper.makeSectionParams(
                    {
                        ...it,
                        blocks: getBlocks([it]),
                    },
                    params,
                ),
            ),
        };

        if (treatmentPlanSection.sections.length === 0) {
            treatmentPlanSection.sections.push({ blocks: treatmentPlanBlocks });
        } else {
            treatmentPlanSection.sections?.[0].blocks.unshift(...treatmentPlanBlocks);
        }

        return {
            ...treatmentPlanSection,
            sections: treatmentPlanSection.sections
                .filter(section => section.blocks.some(block => block.fields.length > 0))
                .map(section => ({
                    ...section,
                    blocks: section.blocks.filter(block => block.fields.length > 0),
                })),
        };
    }

    private getMedicationAndRecommendationBlock(mode: BlockShowingMode): EntryPrintFormBlockData {
        const isCurrentMode = mode === BlockShowingMode.Current;
        const { fields, id: entryId, selectedLang } = this.entry;

        const entryRecommendations = this.entry.medicalCases.getRecommendations().filter(({ parentEntryId, end }) => {
            const isTargetEntry = isCurrentMode ? parentEntryId === entryId : parentEntryId !== entryId;
            const isExpired = end ? DateHelper.isBefore(end, DateHelper.getEndOfDay(Date.now())) : false;

            return isTargetEntry && !isExpired;
        });

        const entryRecommendationsFields = this.makeDataFromRecommendations(entryRecommendations);

        const drugs = this.getDrugs()
            .filter(({ parentEntryId }) => (isCurrentMode ? parentEntryId === entryId : parentEntryId !== entryId))
            .filter(it => it.status === DrugStatus.Active);

        const usedMedications: MedicationField = fields.get(EntryTypeFieldType.UsedMedications) as MedicationField;
        const usedMedicationFields = isCurrentMode
            ? []
            : getVisibleUsedMedications(usedMedications.allActiveMedication, drugs.length > 0).map(med => ({
                  title: DateHelper.makeToDateString(med.end ?? undefined, selectedLang),
                  value: `${usedMedications.getMedicationRevision(med, true)}${
                      med?.frequencyIfReception ? ` ${med?.frequencyIfReception}` : ''
                  }`,
              }));
        const drugsFields = drugs.map(drug => ({
            title: drug.getEndString(selectedLang),
            value: drug.description,
        }));

        return {
            title: isCurrentMode ? '' : this.translateBySelectedLang('medications'),
            subTitle: isCurrentMode ? this.translateBySelectedLang('scheduledAtThisAppointment') : undefined,
            fields: [...usedMedicationFields, ...drugsFields, ...entryRecommendationsFields],
        };
    }

    private makeDataFromRecommendations(recommendations: RecommendationData[]) {
        return recommendations.map(({ id, end, comment: originalComment }) => {
            let comment = originalComment;

            if (this.entry.selectedLang) {
                const recommendationTranslations = this.entry.translations
                    .getExisting(this.entry.selectedLang)
                    .fieldList.get(EntryTypeFieldName.EpisdodesRecommendations);

                comment =
                    recommendationTranslations?.value.find((it: { id: string }) => it.id === id)?.comment ??
                    originalComment;
            }

            return {
                title: end
                    ? `${this.translateBySelectedLang('to')} ${DateHelper.format(end, DateFormat.DayWithMonthString)}`
                    : this.translateBySelectedLang('indefiniteFem'),
                value: comment,
            };
        });
    }

    private getReferralBlock(): EntryPrintFormBlockData {
        const outwardReferrals = this.entry.medicalCases
            .getAllOutwardReferrals()
            .filter(it => isOutwardReferralOfEntry(it, this.entry.id));
        const referralField: ReferralField = this.entry.getField(EntryTypeFieldType.Referrals) as ReferralField;
        const referralList = referralField.getValue();

        const entryReferrals = referralList.values.filter(
            ({ parentEntryId, tiedEntryIds = [], status }) =>
                status !== ReferralStatus.Cancelled &&
                (parentEntryId === this.entry.id ||
                    (tiedEntryIds.includes(this.entry.id) && status !== ReferralStatus.Executed)),
        );

        const ordered = orderBy(
            [
                ...entryReferrals.map(referral => referralToItemParams(referral)),
                ...outwardReferrals.map(outwardReferral => outwardReferralToItemParams(outwardReferral)),
            ],
            'startDate',
        );

        const getOutwardReferralValue = (id: string): string => {
            const outwardReferral = this.entry.medicalCases.getOutwardReferral(id);

            if (!outwardReferral) {
                return '-';
            }

            const title = `${outwardReferral.name} ${outwardReferral.comment ?? ''}`;

            return outwardReferral.executionEntryId === this.entry.id
                ? `${title} (${this.translateBySelectedLang('performed')})`
                : title;
        };

        return {
            title: this.translateBySelectedLang('referrals'),
            fields: ordered.map(item => ({
                title: !!item.endDate
                    ? `${this.translateBySelectedLang('to')} ${DateHelper.format(
                          item.endDate,
                          DateFormat.DayWithMonthString,
                      )}`
                    : this.translateBySelectedLang('indefiniteFem'),
                value:
                    item.type === ItemType.Referral
                        ? referralField.getPrintItemTitle(item.id)
                        : getOutwardReferralValue(item.id),
            })),
        };
    }

    private getDrugs() {
        const { drugList, medicalCases, creationTime, signedAt, id: entryId, selectedLang } = this.entry;
        const {
            medicalCases: { drugsIds },
        } = this.ehr;

        return uniq([...medicalCases.drugsIds, ...drugsIds])
            .map(id => {
                const clonedDrug = cloneDeep(drugList.get(id));

                if (selectedLang && clonedDrug) {
                    const drugTranslations = this.entry.translations
                        .getExisting(selectedLang)
                        .fieldList.get(EntryTypeFieldName.EpisdodesDrugs);

                    clonedDrug.frequencyIfReception = drugTranslations?.value.find(
                        (it: { id: string }) => it.id === id,
                    )?.frequencyIfReception;
                }

                return clonedDrug;
            })
            .filter(isDefined)
            .filter(drug => drug.isActualOnDate(signedAt ?? creationTime) || drug.isBelongToEntry(entryId));
    }

    private getAssessmentEpisodeBlocks(): EntryPrintFormBlockData[] {
        return this.entry.medicalCases.ids.map(id => this.makeEpisodeBlock(id));
    }

    private makeEpisodeBlock(medicalCaseId: string): EntryPrintFormBlockData {
        const { dictionary } = store;
        const { selectedLang } = this.entry;
        const icdDictionary = dictionary.getDictionary(DictionaryNames.Mkb10);

        const medicalCase = this.entry.medicalCases.getExisting(medicalCaseId);
        const user = getCachedUserById(queryClient, medicalCase.managerId ?? medicalCase.authorId);
        const userSpecialty = user?.mainSpecialtyId ? getSpecialtyById(user.mainSpecialtyId) : undefined;
        const userSpecialtyName = selectedLang
            ? (userSpecialty?.nameTranslations?.find(it => it.locale === selectedLang)?.value ?? userSpecialty?.name)
            : userSpecialty?.name;

        const episodeManagerString = [user && getFullName(user), userSpecialtyName].filter(Boolean).join(', ');

        return {
            title: `${this.translateBySelectedLang('medicalEpisode')}: ${medicalCase.name}`,
            fields: [
                {
                    title: this.translateBySelectedLang('status'),
                    value: selectedLang ? medicalCase.getStatusStingByLocale(selectedLang) : medicalCase.statusSting,
                    fieldOptions: {
                        occupiedLineWidth: 'TwoPerLineField',
                    },
                },
                {
                    title: this.translateBySelectedLang('episodeManager'),
                    value: episodeManagerString,
                    fieldOptions: {
                        occupiedLineWidth: 'TwoPerLineField',
                    },
                },
                ...medicalCase.diagnoses.flatMap(({ icdId, clinicalDiagnosis }) => {
                    const dictionaryItem = icdDictionary.get(icdId);

                    return [
                        {
                            title: this.translateBySelectedLang('ICD'),
                            value: dictionaryItem ? this.getDictionaryTitle(dictionaryItem) : '',
                        },
                        {
                            title: this.translateBySelectedLang('diagnosisDescription'),
                            value: clinicalDiagnosis,
                        },
                    ];
                }),
            ],
        };
    }

    private makeEntryDetail(): EntryPrintFormFieldsData[] {
        const { doctorId, authorId, isShowingAuthor, timeByTz } = getEntryExtraData();

        // Users (and the profile with the eng name) are prefetched into the RQ cache by
        // entryPrintFormService before the print data is built.
        const doctor = doctorId ? getCachedUserById(queryClient, doctorId) : undefined;
        const author = authorId ? getCachedUserById(queryClient, authorId) : undefined;
        const user = doctor ?? author;

        let userName = '';
        const lang = this.entry.selectedLang || store.authUser.selectedLanguage;

        if (lang === Locale.Ru || lang === Locale.Am) {
            userName = user ? getFullName(user) : '';
        } else {
            const profile = user
                ? queryClient.getQueryData(userProfileQueryOptions.byUserId(user.id).queryKey)
                : undefined;
            const engFullName = profile
                ? formatFullName({
                      firstName: profile.engFirstName,
                      lastName: profile.engLastName,
                      oldLastName: profile.engOldLastName,
                      patronymic: profile.engPatronymic,
                  })
                : '';
            userName = !!engFullName.trim() ? engFullName : user ? getFullName(user) : '';
        }

        return isShowingAuthor
            ? [
                  {
                      title: this.translateBySelectedLang(doctor && isDoctor(doctor) ? 'doctor' : 'author'),
                      value: userName,
                  },
                  {
                      title: this.translateBySelectedLang('dateOfReception'),
                      value: timeByTz,
                  },
              ]
            : [
                  {
                      title: this.translateBySelectedLang('dateOfReception'),
                      value: timeByTz,
                  },
              ];
    }

    private translateBySelectedLang(value: TranslationValues): string {
        const { selectedLang } = this.entry;

        return getTranslate(value, selectedLang);
    }

    private getDictionaryTitle(item: DictionaryParams): string {
        const { selectedLang } = this.entry;

        if (!selectedLang) {
            return `${item.value} ${item.title}`;
        }

        return `${item.value} ${item.titleTranslations?.find(it => it.locale === selectedLang)?.title ?? item.title}`;
    }
}

function getBlocks(sections: EntryTypeSection[]) {
    return sections.flatMap(({ blocks }) => blocks);
}

export const entryPrintFormDataHelper = new EntryPrintFormDataHelper();
